import type { U3141ImportChainAnnotationsDryRunLot, U3141U2200Job } from '../U3141Service';

export type U3141ImportChainAnnotationsDryRunLotStatus =
  | 'identical'
  | 'different'
  | 'missing_index'
  | 'no_shapes_in_pdf';

/** Cases « Écraser » par défaut : cochées pour les lots au statut `different` (BL-0700). */
export function u3141BuildDefaultOverwriteByLotIdForDryRun(
  lots: U3141ImportChainAnnotationsDryRunLot[],
): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const lot of lots) {
    map[lot.lotDetailId] = lot.status === 'different';
  }
  return map;
}

/** Extrait les UUID cochés pour l'apply (BL-0700). */
export function u3141CollectLotDetailIdsToReplace(overwriteByLotId: Record<string, boolean>): string[] {
  return Object.entries(overwriteByLotId)
    .filter(([, checked]) => checked)
    .map(([lotDetailId]) => lotDetailId);
}

export function u3141ParseImportChainAnnotationsDryRunResponse(data: unknown): {
  sourceDocumentId: string;
  masterLotChainId: string;
  lots: U3141ImportChainAnnotationsDryRunLot[];
} | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown };
  const raw = envelope.result && typeof envelope.result === 'object' ? envelope.result : data;
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const sourceDocumentId = row.sourceDocumentId;
  const masterLotChainId = row.masterLotChainId;
  const lotsRaw = row.lots;
  if (typeof sourceDocumentId !== 'string' || typeof masterLotChainId !== 'string' || !Array.isArray(lotsRaw)) {
    return null;
  }
  const lots: U3141ImportChainAnnotationsDryRunLot[] = [];
  for (const entry of lotsRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const lot = entry as Record<string, unknown>;
    if (typeof lot.lotDetailId !== 'string' || typeof lot.lotLabel !== 'string') continue;
    if (typeof lot.status !== 'string') continue;
    if (typeof lot.existingShapeCount !== 'number' || typeof lot.extractedShapeCount !== 'number') continue;
    lots.push({
      lotDetailId: lot.lotDetailId,
      lotLabel: lot.lotLabel,
      status: lot.status as U3141ImportChainAnnotationsDryRunLotStatus,
      existingShapeCount: lot.existingShapeCount,
      extractedShapeCount: lot.extractedShapeCount,
    });
  }
  return { sourceDocumentId, masterLotChainId, lots };
}

export type U3141ImportChainAnnotationsJobSummary = {
  lotsAppliques: string[];
  lotsIgnores: string[];
  lotsIdentiquesNonDemandes: string[];
};

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/** Résumé job BL-0700 depuis result.data. */
export function u3141ExtractImportChainAnnotationsSummaryFromJob(
  job: U3141U2200Job | null,
): U3141ImportChainAnnotationsJobSummary {
  const data = job?.result?.data;
  if (!data || typeof data !== 'object') {
    return { lotsAppliques: [], lotsIgnores: [], lotsIdentiquesNonDemandes: [] };
  }
  const row = data as Record<string, unknown>;
  return {
    lotsAppliques: readStringArray(row.lotsAppliques),
    lotsIgnores: readStringArray(row.lotsIgnores),
    lotsIdentiquesNonDemandes: readStringArray(row.lotsIdentiquesNonDemandes),
  };
}
